import React, { useEffect, useRef, useState } from "react";
import { InteractiveStory } from "../../../story/forestRescueStory";
import { ProgressControls } from "./ProgressControls";
import { StoryPage } from "./StoryPage";

type StoryPlayerProps = {
  story: InteractiveStory;
};

export function StoryPlayer({ story }: StoryPlayerProps) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const touchStartX = useRef<number | null>(null);
  const touchStartY = useRef<number | null>(null);
  const currentScene = story.scenes[currentIndex] || story.scenes[0];
  const currentPosition = currentIndex + 1;
  const canGoBack = currentIndex > 0;
  const canGoNext = currentIndex < story.scenes.length - 1;

  useEffect(() => {
    [story.scenes[currentIndex - 1], story.scenes[currentIndex + 1]]
      .filter(Boolean)
      .forEach((scene) => {
        const image = new Image();
        image.src = scene.image;
      });
  }, [currentIndex, story.scenes]);

  const goToPreviousPage = () => {
    setCurrentIndex((previousIndex) => Math.max(previousIndex - 1, 0));
  };

  const goToNextPage = () => {
    setCurrentIndex((previousIndex) => Math.min(previousIndex + 1, story.scenes.length - 1));
  };

  const restart = () => {
    setCurrentIndex(0);
  };

  const handleTouchStart = (event: React.TouchEvent<HTMLDivElement>) => {
    const firstTouch = event.touches[0];
    touchStartX.current = firstTouch.clientX;
    touchStartY.current = firstTouch.clientY;
  };

  const handleTouchMove = (event: React.TouchEvent<HTMLDivElement>) => {
    if (touchStartX.current === null || touchStartY.current === null) {
      return;
    }

    const currentTouch = event.touches[0];
    const deltaX = currentTouch.clientX - touchStartX.current;
    const deltaY = currentTouch.clientY - touchStartY.current;
    const isHorizontalSwipe = Math.abs(deltaX) > 12 && Math.abs(deltaX) > Math.abs(deltaY);

    if (isHorizontalSwipe) {
      event.preventDefault();
    }
  };

  const handleTouchEnd = (event: React.TouchEvent<HTMLDivElement>) => {
    if (touchStartX.current === null || touchStartY.current === null) {
      return;
    }

    const changedTouch = event.changedTouches[0];
    const deltaX = changedTouch.clientX - touchStartX.current;
    const deltaY = changedTouch.clientY - touchStartY.current;
    const isHorizontalSwipe = Math.abs(deltaX) > 50 && Math.abs(deltaX) > Math.abs(deltaY);

    if (isHorizontalSwipe && deltaX < 0) {
      goToNextPage();
    }

    if (isHorizontalSwipe && deltaX > 0) {
      goToPreviousPage();
    }

    touchStartX.current = null;
    touchStartY.current = null;
  };

  return (
    <div
      className="story-player"
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
    >
      <header className="story-header">
        <div>
          <span className="story-eyebrow">Swipe Ebook</span>
          <h1>{story.title}</h1>
        </div>
        <ProgressControls
          canGoBack={canGoBack}
          canGoNext={canGoNext}
          currentPosition={currentPosition}
          totalScenes={story.scenes.length}
          onBack={goToPreviousPage}
          onNext={goToNextPage}
          onRestart={restart}
        />
      </header>

      <StoryPage
        scene={currentScene}
        storyTitle={story.title}
      />
    </div>
  );
}
